import type { PlayerInput } from '../shared/protocol';
import { NETWORK } from '../shared/constants';
import { useGameStore, type RichWorldSnapshot } from '../state/useGameStore';
import { PACK_IDS } from '../shared/game';
import type { MapId, SiegeGameState } from '../shared/mapData';
import { normalizePingRequest, normalizeTeamCommand } from '../shared/teamplay';

type Vec3 = { x: number; y: number; z: number };

let socket: WebSocket | null = null;
let reconnectTimer: number | null = null;
let reconnectDelayMs = 1000;
const RECONNECT_MAX_DELAY_MS = 16000;
let reconnectEnabled = true;
let lifecycleListenersInstalled = false;
const CLIENT_KEY_STORAGE_KEY = 'tribes2.clientKey.v1';
const RECONNECT_CREDENTIAL_STORAGE_KEY = 'tribes2.reconnectCredential.v1';
const LOBBY_PREFERENCES_STORAGE_KEY = 'tribes2.lobbyPreferences.v1';
let cachedClientKey: string | null = null;
let cachedReconnectCredential: { clientKey: string; token: string } | null = null;

export type LobbyPreferences = {
  displayName: string;
  preferredTeam: 'auto' | 'red' | 'blue';
  preferredMap: MapId;
  botPreset: 'training' | 'skirmish' | 'full';
};

const DEFAULT_LOBBY_PREFERENCES: LobbyPreferences = {
  displayName: '',
  preferredTeam: 'auto',
  preferredMap: 'katabatic',
  botPreset: 'full',
};

let predictionOffset: Vec3 = { x: 0, y: 0, z: 0 };
let lastPredictedPosition: Vec3 = { x: 0, y: 0, z: 0 };
let predictionBlendWeight = 0.78;
const PREDICTION_SNAP_THRESHOLD = 2;
const PREDICTION_BLEND_WEIGHT = 0.3;

const pendingInputs: PlayerInput[] = [];
export const MAX_PENDING_INPUTS = 120;

function getProtocol() {
  if (typeof window === 'undefined') {
    return 'ws';
  }

  return window.location.protocol === 'https:' ? 'wss' : 'ws';
}

function getExplicitServerUrl() {
  const env = (import.meta as ImportMeta & { env?: Record<string, string | undefined> }).env;
  return env?.VITE_TRIBES2_WS_URL?.trim() || null;
}

function isLocalHost(hostname: string) {
  return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]' || hostname === '::1';
}

function shouldUseStandaloneSession() {
  if (typeof window === 'undefined') {
    return false;
  }

  return !getExplicitServerUrl() && !isLocalHost(window.location.hostname);
}

function sanitizeLobbyPreferences(preferences?: Partial<LobbyPreferences>): LobbyPreferences {
  const displayName = preferences?.displayName?.replace(/\s+/g, ' ').trim().slice(0, 24) ?? DEFAULT_LOBBY_PREFERENCES.displayName;
  const preferredTeam = preferences?.preferredTeam === 'red' || preferences?.preferredTeam === 'blue' ? preferences.preferredTeam : 'auto';
  const preferredMap = preferences?.preferredMap === 'dangerouscrossing' || preferences?.preferredMap === 'raindance'
    ? preferences.preferredMap
    : 'katabatic';
  const botPreset = preferences?.botPreset === 'training' || preferences?.botPreset === 'skirmish' ? preferences.botPreset : 'full';
  return { displayName, preferredTeam, preferredMap, botPreset };
}

export function getLobbyPreferences(): LobbyPreferences {
  if (typeof window === 'undefined') {
    return DEFAULT_LOBBY_PREFERENCES;
  }

  try {
    const stored = window.localStorage.getItem(LOBBY_PREFERENCES_STORAGE_KEY);
    return sanitizeLobbyPreferences(stored ? JSON.parse(stored) as Partial<LobbyPreferences> : undefined);
  } catch {
    return DEFAULT_LOBBY_PREFERENCES;
  }
}

export function setLobbyPreferences(preferences: Partial<LobbyPreferences>): LobbyPreferences {
  const sanitized = sanitizeLobbyPreferences(preferences);
  if (typeof window !== 'undefined') {
    try {
      window.localStorage.setItem(LOBBY_PREFERENCES_STORAGE_KEY, JSON.stringify(sanitized));
    } catch {
      // Storage is optional; connection still uses the sanitized in-memory values.
    }
  }
  return sanitized;
}

export function getGameServerUrl(lobbyPreferences?: Partial<LobbyPreferences>) {
  const explicitUrl = getExplicitServerUrl();
  if (explicitUrl) {
    const url = new URL(explicitUrl);
    addClientCredentials(url);
    const preferences = sanitizeLobbyPreferences(lobbyPreferences ?? getLobbyPreferences());
    if (preferences.displayName) {
      url.searchParams.set('displayName', preferences.displayName);
    }
    url.searchParams.set('preferredTeam', preferences.preferredTeam);
    url.searchParams.set('preferredMap', preferences.preferredMap);
    url.searchParams.set('botPreset', preferences.botPreset);
    return url.toString();
  }

  if (typeof window === 'undefined') {
    return `${getProtocol()}://localhost:${NETWORK.serverPort}`;
  }

  const url = new URL(`${getProtocol()}://${window.location.hostname}:${NETWORK.serverPort}`);
  addClientCredentials(url);
  const requestedMap = new URLSearchParams(window.location.search).get('mapId');
  if (requestedMap === 'katabatic' || requestedMap === 'dangerouscrossing' || requestedMap === 'raindance') {
    url.searchParams.set('mapId', requestedMap);
  }

  const preferences = sanitizeLobbyPreferences(lobbyPreferences ?? getLobbyPreferences());
  if (preferences.displayName) {
    url.searchParams.set('displayName', preferences.displayName);
  }
  url.searchParams.set('preferredTeam', preferences.preferredTeam);
  url.searchParams.set('preferredMap', preferences.preferredMap);
  url.searchParams.set('botPreset', preferences.botPreset);

  return url.toString();
}

function isOnline() {
  return typeof navigator === 'undefined' ? true : navigator.onLine;
}

function getStoredClientKey() {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const value = window.localStorage.getItem(CLIENT_KEY_STORAGE_KEY);
    return typeof value === 'string' && value.length > 0 ? value : null;
  } catch {
    return null;
  }
}

function setStoredClientKey(value: string) {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(CLIENT_KEY_STORAGE_KEY, value);
  } catch {
    // Ignore storage failures and keep the in-memory client key stable.
  }
}

function generateClientKey() {
  if (typeof window !== 'undefined') {
    const crypto = window.crypto;
    if (crypto && typeof crypto.randomUUID === 'function') {
      return `tribes2-${crypto.randomUUID()}`;
    }
  }

  return `tribes2-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function getPersistentClientKey() {
  if (cachedClientKey) {
    return cachedClientKey;
  }

  const storedClientKey = getStoredClientKey();
  if (storedClientKey) {
    cachedClientKey = storedClientKey;
    return storedClientKey;
  }

  const generatedClientKey = generateClientKey();
  cachedClientKey = generatedClientKey;
  setStoredClientKey(generatedClientKey);
  return generatedClientKey;
}

function getReconnectCredential() {
  if (typeof window !== 'undefined') {
    try {
      const stored = window.localStorage.getItem(RECONNECT_CREDENTIAL_STORAGE_KEY);
      if (stored) {
        const credential = JSON.parse(stored) as { clientKey?: unknown; token?: unknown };
        if (typeof credential.clientKey === 'string' && typeof credential.token === 'string') {
          cachedReconnectCredential = { clientKey: credential.clientKey, token: credential.token };
        }
      }
    } catch {
      // In-memory proof still supports reconnect when storage is unavailable.
    }
  }
  return cachedReconnectCredential;
}

function addClientCredentials(url: URL) {
  const clientKey = getPersistentClientKey();
  url.searchParams.set('clientKey', clientKey);
  const credential = getReconnectCredential();
  if (credential?.clientKey === clientKey) {
    url.searchParams.set('reconnectToken', credential.token);
  }
}

function acceptServerCredentials(clientKey: unknown, token: unknown) {
  if (typeof clientKey !== 'string' || !clientKey || clientKey.length > 128 ||
      typeof token !== 'string' || !token || token.length > 128) return;
  cachedClientKey = clientKey;
  cachedReconnectCredential = { clientKey, token };
  setStoredClientKey(clientKey);
  try {
    window.localStorage.setItem(RECONNECT_CREDENTIAL_STORAGE_KEY, JSON.stringify(cachedReconnectCredential));
  } catch {
    // In-memory proof still supports reconnect when storage is unavailable.
  }
}

function clearReconnectTimer() {
  if (reconnectTimer !== null) {
    window.clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
}

function scheduleReconnect() {
  if (!reconnectEnabled || !isOnline() || reconnectTimer !== null) {
    return;
  }

  useGameStore.getState().setConnectionState('reconnecting');
  const delay = reconnectDelayMs;
  reconnectDelayMs = Math.min(reconnectDelayMs * 2, RECONNECT_MAX_DELAY_MS);
  reconnectTimer = window.setTimeout(() => {
    reconnectTimer = null;
    if (reconnectEnabled) {
      connectGameServer();
    }
  }, delay);
}

function handleOnline() {
  if (
    reconnectEnabled &&
    (!socket || socket.readyState === WebSocket.CLOSED || socket.readyState === WebSocket.CLOSING)
  ) {
    connectGameServer();
  }
}

function handleOffline() {
  useGameStore.getState().setConnectionState('offline');
}

function ensureLifecycleListeners() {
  if (lifecycleListenersInstalled || typeof window === 'undefined') {
    return;
  }

  window.addEventListener('online', handleOnline);
  window.addEventListener('offline', handleOffline);
  lifecycleListenersInstalled = true;
}

function inferPackSelection(input: PlayerInput) {
  if (input.packSelection != null) {
    return input.packSelection;
  }

  if (typeof input.packSlot === 'number') {
    return PACK_IDS[Math.max(0, Math.min(PACK_IDS.length - 1, Math.round(input.packSlot) - 1))];
  }

  return undefined;
}

function isSnapshotMessage(value: unknown): value is RichWorldSnapshot {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const snapshot = value as Partial<RichWorldSnapshot>;
  return (
    Number.isFinite(snapshot.serverTick) &&
    Array.isArray(snapshot.players) &&
    Array.isArray(snapshot.projectiles) &&
    snapshot.match !== null &&
    typeof snapshot.match === 'object' &&
    Array.isArray(snapshot.flags) &&
    Array.isArray(snapshot.boostPads) &&
    Array.isArray(snapshot.generators) &&
    Array.isArray(snapshot.sensors) &&
    Array.isArray(snapshot.shieldDoors) &&
    Array.isArray(snapshot.inventoryStations) &&
    Array.isArray(snapshot.repairZones) &&
    Array.isArray(snapshot.turrets) &&
    Array.isArray(snapshot.effects) &&
    Array.isArray(snapshot.events)
  );
}

export function connectGameServer(lobbyPreferences?: Partial<LobbyPreferences>) {
  ensureLifecycleListeners();
  reconnectEnabled = true;

  if (typeof window === 'undefined') {
    return null;
  }

  if (useGameStore.getState().connectionState === 'standalone') {
    reconnectEnabled = false;
    clearReconnectTimer();
    return null;
  }

  if (shouldUseStandaloneSession()) {
    reconnectEnabled = false;
    clearReconnectTimer();
    useGameStore.getState().startStandaloneSession(lobbyPreferences ?? getLobbyPreferences());
    return null;
  }

  if (socket && socket.readyState <= WebSocket.OPEN) {
    return socket;
  }

  clearReconnectTimer();
  useGameStore.getState().setConnectionState(socket ? 'connecting' : reconnectTimer ? 'reconnecting' : 'connecting');

  if (!isOnline()) {
    useGameStore.getState().setConnectionState('offline');
    return null;
  }

  const nextSocket = new WebSocket(getGameServerUrl(lobbyPreferences));
  socket = nextSocket;

  nextSocket.addEventListener('open', () => {
    if (socket !== nextSocket) {
      return;
    }

    reconnectDelayMs = 1000;
    clearReconnectTimer();
    predictionOffset = { x: 0, y: 0, z: 0 };
    lastPredictedPosition = { x: 0, y: 0, z: 0 };
    predictionBlendWeight = 0.78;
    for (const pending of pendingInputs) {
      if (nextSocket.readyState === WebSocket.OPEN) {
        nextSocket.send(JSON.stringify({ type: 'input', input: pending }));
      }
    }
    pendingInputs.length = 0;
    useGameStore.getState().setConnectionState('online');
  });

  nextSocket.addEventListener('close', () => {
    if (socket !== nextSocket) {
      return;
    }

    socket = null;
    useGameStore.getState().setConnectionState(isOnline() ? 'reconnecting' : 'offline');
    if (reconnectEnabled) {
      scheduleReconnect();
    }
  });

  nextSocket.addEventListener('error', () => {
    if (socket !== nextSocket) {
      return;
    }

    useGameStore.getState().setConnectionState(isOnline() ? 'reconnecting' : 'offline');
    if (!isOnline()) {
      clearReconnectTimer();
    }
  });

  nextSocket.addEventListener('message', (event) => {
    let message:
      | { type: 'welcome'; id: string; clientKey?: unknown; reconnectToken?: unknown }
      | { type: 'snapshot'; snapshot: RichWorldSnapshot }
      | { type: 'chat'; msg: unknown }
      | { type: 'siege_state'; state: SiegeGameState }
      | null = null;

    try {
      message = JSON.parse(String(event.data)) as
        | { type: 'welcome'; id: string; clientKey?: unknown; reconnectToken?: unknown }
        | { type: 'snapshot'; snapshot: RichWorldSnapshot }
        | { type: 'chat'; msg: unknown }
        | { type: 'siege_state'; state: SiegeGameState };
    } catch {
      return;
    }

    if (!message || typeof message !== 'object' || typeof message.type !== 'string') {
      return;
    }

    if (message.type === 'welcome') {
      acceptServerCredentials(message.clientKey, message.reconnectToken);
      if (typeof message.id === 'string' && message.id.length > 0) {
        useGameStore.getState().setWelcome(message.id);
      }
      return;
    }

    if (message.type === 'chat' && message.msg && typeof message.msg === 'object') {
      const chatMsg = message.msg as Record<string, unknown>;
      if (
        typeof chatMsg.id === 'string' &&
        typeof chatMsg.playerId === 'string' &&
        typeof chatMsg.playerName === 'string' &&
        typeof chatMsg.text === 'string' &&
        typeof chatMsg.timestamp === 'number'
      ) {
        useGameStore.getState().addChatMessage({
          id: chatMsg.id,
          playerId: chatMsg.playerId,
          playerName: chatMsg.playerName,
          team: (chatMsg.team === 'storm' || chatMsg.team === 'blood_eagle' ? chatMsg.team : 'all') as 'all' | 'storm' | 'blood_eagle',
          text: chatMsg.text,
          timestamp: chatMsg.timestamp,
        });
      }
      return;
    }

    if (message.type === 'snapshot' && isSnapshotMessage(message.snapshot)) {
      const nextPredictionBlendWeight = reconcilePrediction(message.snapshot);
      useGameStore.getState().applySnapshot(message.snapshot, nextPredictionBlendWeight);
    }

    if (message.type === 'siege_state' && message.state && typeof message.state === 'object') {
      useGameStore.getState().setSiegeState(message.state);
    }
  });

  return nextSocket;
}

export function disconnectGameServer() {
  reconnectEnabled = false;
  clearReconnectTimer();

  if (socket) {
    const nextSocket = socket;
    socket = null;
    nextSocket.close();
  }

  useGameStore.getState().setConnectionState('offline');
}

export function applyPrediction(localPlayer: { position: Vec3 }, input: PlayerInput, dt: number) {
  predictionOffset.x *= 1 - 2 * dt;
  predictionOffset.y *= 1 - 2 * dt;
  predictionOffset.z *= 1 - 2 * dt;

  lastPredictedPosition = {
    x: localPlayer.position.x,
    y: localPlayer.position.y,
    z: localPlayer.position.z,
  };

  return {
    x: lastPredictedPosition.x + predictionOffset.x,
    y: lastPredictedPosition.y + predictionOffset.y,
    z: lastPredictedPosition.z + predictionOffset.z,
  };
}

function reconcilePrediction(snapshot: RichWorldSnapshot) {
  const store = useGameStore.getState();
  const localId = store.localId;
  if (!localId) return;

  const serverPlayer = Array.isArray(snapshot.players)
    ? (snapshot.players as { id?: string; position?: Vec3 }[]).find((p) => p.id === localId)
    : undefined;
  if (!serverPlayer?.position) return;

  const dx = lastPredictedPosition.x - serverPlayer.position.x;
  const dy = lastPredictedPosition.y - serverPlayer.position.y;
  const dz = lastPredictedPosition.z - serverPlayer.position.z;
  const error = Math.sqrt(dx * dx + dy * dy + dz * dz);

  const nextPredictionBlendWeight = error > PREDICTION_SNAP_THRESHOLD
    ? 0
    : error > 0.5
      ? 0.45
      : 0.78;

  if (nextPredictionBlendWeight !== predictionBlendWeight) {
    predictionBlendWeight = nextPredictionBlendWeight;
  }

  if (error > PREDICTION_SNAP_THRESHOLD) {
    predictionOffset = { x: 0, y: 0, z: 0 };
  } else {
    predictionOffset = {
      x: dx * PREDICTION_BLEND_WEIGHT,
      y: dy * PREDICTION_BLEND_WEIGHT,
      z: dz * PREDICTION_BLEND_WEIGHT,
    };
  }

  return predictionBlendWeight;
}

export function reconcilePredictionForTest(snapshot: RichWorldSnapshot) {
  reconcilePrediction(snapshot);
}

export function getPredictionState() {
  return { predictionOffset: { ...predictionOffset }, lastPredictedPosition: { ...lastPredictedPosition } };
}

export function getReconnectDelay() {
  return reconnectDelayMs;
}

export function getPendingInputCount() {
  return pendingInputs.length;
}

export function getPendingInputTicksForTest() {
  return pendingInputs.map((input) => input.tick);
}

export function clearPendingInputsForTest() {
  pendingInputs.length = 0;
}

export function sendInput(input: PlayerInput) {
  if (!socket || socket.readyState !== WebSocket.OPEN) {
    if (pendingInputs.length >= MAX_PENDING_INPUTS) {
      pendingInputs.shift();
    }
    pendingInputs.push(input);
    return;
  }

  const command = normalizeTeamCommand(input.commandRequest ?? input.directiveCall) ?? undefined;
  const ping = normalizePingRequest(input.pingRequest ?? input.tacticalPing);

  const payloadInput = {
    ...input,
    commandRequest: command,
    directiveCall: command,
    pingRequest: ping,
    tacticalPing: ping,
    packSelection: inferPackSelection(input),
    grenadeThrow: Boolean((input as PlayerInput & { grenadeThrow?: boolean }).grenadeThrow),
    grenadeCycle: Boolean((input as PlayerInput & { grenadeCycle?: boolean }).grenadeCycle),
  };
  socket.send(JSON.stringify({ type: 'input', input: payloadInput }));
}

export function sendMessage(msg: unknown) {
  if (!socket || socket.readyState !== WebSocket.OPEN) {
    return;
  }
  socket.send(JSON.stringify(msg));
}
